import React, { useEffect, useState } from 'react';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Pagination } from '../../../clients/quipli-service/pagination/pagination';
import { DateTime } from 'luxon';
import { QuipliLink } from '../../../components/link/quipli-link';
import FileSaver from 'file-saver';
import {
  PaymentReportRequest,
  PaymentReportRow,
  PaymentReportSummary,
} from '../../../clients/quipli-service/report/report';
import { Location } from '../../../clients/quipli-service/location/location';
import { useReportApiV2 } from '../../../clients/quipli-service/report/report-api-v2';
import { Spinner } from '../../../components/spinner/spinner';
import { Typography } from '../../../components/typography/typography';
import { Button } from '../../../components/form/button';
import Icon from '../../../components/icons/icon';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { Card } from '../../../components/card/card';
import { useSearchParams } from 'react-router-dom';
import { displayMoneyFormat } from '../../../util/util';
import { PaymentMethodFormat } from '../../collectibles/invoice-list';
import './payments-report-v2.scss';
import { PaymentStatus } from '../../../clients/quipli-service/payment/payment';
import { StatusBadge } from '../../../components/status-badge/status-badge';
import _ from 'lodash';
import { Paginator } from '../../../components/paginator/paginator-v2';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { SpinnerModal } from '../../../components/modal/spinner-modal';
import { toast } from '../../../util/toasts';

function extractPaymentReportSearchParams(params: URLSearchParams): PaymentReportRequest {
  return {
    from: params.get('from') ? DateTime.fromISO(params.get('from')!) : undefined,
    to: params.get('to') ? DateTime.fromISO(params.get('to')!) : undefined,
    pageNumber: params.get('pageNumber') ? parseInt(params.get('pageNumber')!) : undefined,
    pageSize: params.get('pageSize') ? parseInt(params.get('pageSize')!) : undefined,
  } as PaymentReportRequest;
}

export const PaymentsReportV2 = () => {
  const { paymentsReport, exportPaymentsReport, paymentsSummary } = useReportApiV2();
  const { getMultipleConfigurations } = useAPI();
  const [timezone, setTimezone] = useState<string>();
  const [currency, setCurrency] = useState<string>();
  const [paymentReport, setPaymentReport] = useState<Pagination<PaymentReportRow>>();
  const [summary, setSummary] = useState<PaymentReportSummary>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  // List searchRequest hooks
  const [params] = useSearchParams();
  const { request: reportRequest, setRequest: setReportRequest } = useRequestQueryParams<PaymentReportRequest>(
    extractPaymentReportSearchParams(params),
    async () => {
      if (timezone && Object.keys(reportRequest).length > 0) {
        setIsLoading(true);
        const [report, summary] = await Promise.all([paymentsReport(reportRequest), paymentsSummary(reportRequest)]);
        setPaymentReport(report);
        setSummary(summary);
        setIsLoading(false);
      }
    },
    [timezone],
  );

  // Callbacks

  const loadTimezone = async () => {
    const confs = await getMultipleConfigurations(['CONF_TIMEZONE', 'CONF_CURRENCY']);
    const timezone = confs.find((c) => c.variable === 'CONF_TIMEZONE')?.value;
    const currency = confs.find((c) => c.variable === 'CONF_CURRENCY')?.value;
    setCurrency(currency || 'USD');
    if (timezone) {
      setTimezone(timezone);
      if (!reportRequest.to && !reportRequest.from) {
        setReportRequest((req) => {
          return {
            ...req,
            from: DateTime.local({ zone: timezone }).startOf('day'),
            to: DateTime.local({ zone: timezone }).endOf('day'),
          };
        });
      }
    }
  };

  // Hooks
  useEffect(() => {
    loadTimezone();
  }, []);

  const exportReport = async () => {
    setIsExporting(true);
    try {
      const file = await exportPaymentsReport({ from: reportRequest.from, to: reportRequest.to });

      const exportFileName = `Payments_Report${
        reportRequest.from ? '_' + reportRequest.from?.toFormat('D').replaceAll('/', '-') : ''
      }`;
      const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
      const fileExtension = '.xlsx';

      const data = new Blob([file], { type: fileType });
      FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
    } catch (e) {
      console.error(e);
      toast('Failed to export report', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="payments-report-v2 flex flex-column gap-4">
      <div className="payments-report-v2 flex flex-wrap item-start">
        <div className="m-[2px] flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(25%-4px)]">
          <Typography type="text-md" weight="medium" className="text-[var(--grey-700)]">
            From
          </Typography>
          <DatePicker
            value={reportRequest.from}
            onChange={(from) =>
              setReportRequest((prevState) => ({
                ...prevState,
                from: from?.setZone(timezone, { keepLocalTime: true }).startOf('day'),
              }))
            }
            clearable={true}
          />
        </div>
        <div className="m-[2px] flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(25%-4px)]">
          <Typography type="text-md" weight="medium" className="text-[var(--grey-700)]">
            To
          </Typography>
          <DatePicker
            value={reportRequest.to}
            onChange={(to) =>
              setReportRequest((prevState) => ({
                ...prevState,
                to: to?.setZone(timezone, { keepLocalTime: true }).endOf('day'),
              }))
            }
            clearable={true}
          />
        </div>
        <div className="m-[2px] self-end flex flex-col flex-[0_1_calc(50%-4px)] lg:flex-[0_1_calc(50%-4px)]">
          <Button
            className="self-start lg:self-end	button-yellow !border-0 w-[114px] h-[40px]"
            onClick={() => exportReport()}
            data-cy="export"
          >
            <Typography
              type="text-md"
              weight="semi-bold"
              className="flex justify-center justify-items-center gap-1 text-[var(--grey-700)]"
            >
              Export
            </Typography>
            <Icon icon="Export" size="medium" stroke="var(--grey-700)" />
          </Button>
        </div>
      </div>
      {summary && currency && <SummaryStats summary={summary} currency={currency} />}
      <div className="content-wrapper">
        <div className="table-wrapper">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left">Customer Name</th>
                <th className="text-right">Amount</th>
                <th className="text-left">Invoice Number</th>
                <th className="text-left">Payment Method</th>
                <th className="text-left">Date</th>
                <th className="text-left">Location</th>
                <th className="text-left">Type</th>
                <th className="text-left">Status</th>
                <th className="text-left">Added By</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={100}>
                    <Spinner />
                  </td>
                </tr>
              ) : (
                <>
                  {currency && paymentReport && (
                    <>
                      {paymentReport.items.length > 0 ? (
                        paymentReport.items.map((report, i) => <TableRow key={i} report={report} currency={currency} />)
                      ) : (
                        <tr>
                          <td colSpan={7}>No record found</td>
                        </tr>
                      )}
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
        <Paginator
          pagination={paymentReport?.pagination}
          request={reportRequest}
          setRequest={(pag) =>
            setReportRequest((req) => {
              return { ...req, ...pag };
            })
          }
        />
      </div>
      <SpinnerModal isOpen={isExporting} contentDescription={'Exporting Payment Report'} />
    </div>
  );
};

const TableRow = ({ key, report, currency }: { key: number; report: PaymentReportRow; currency: string }) => {
  return (
    <tr key={key}>
      <td>
        <QuipliLink
          className="underline underline-offset-1 text-[var(--quipli-black-800)]"
          to={`users/customer_form/${report['Customer ID']}`}
        >
          {report['Customer Name']}
        </QuipliLink>
      </td>
      <td className="text-right">{displayMoneyFormat(report['Amount'], currency)}</td>
      <td>
        <QuipliLink
          className="underline underline-offset-1 text-[var(--quipli-black-800)]"
          to={`orders/view/${report['Order ID']}`}
        >
          {report['Invoice Number']}
        </QuipliLink>
      </td>
      <td>{PaymentMethodFormat(report['Method'], report['Method Detail'])}</td>
      <td className="Date">
        <Typography type="text-sm" weight="regular" className="text-[var(--quipli-black-800)]">
          {DateTime.fromISO(report['Date']).toFormat('LLL-dd')}
        </Typography>
        <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
          {report['Time']}
        </Typography>
      </td>
      <td>{report['Location']}</td>
      <td>{report['Type']}</td>
      <td>{PaymentStatusBadge(report['Status'])}</td>
      <td>{report['Added By']}</td>
    </tr>
  );
};

export const PaymentStatusBadge = (status: string) => {
  switch (status) {
    case PaymentStatus.succeeded:
      return <StatusBadge label="Succeeded" type="green" />;
    case PaymentStatus.failed:
      return <StatusBadge label="Failed" type="red" />;
    default:
      return <StatusBadge label={_.startCase(status)} type="yellow" />;
  }
};

export const SummaryStats = ({ summary, currency }: { summary: PaymentReportSummary; currency: string }) => {
  const [locations, setLocations] = useState<Location[]>();
  const { listLocations } = useAPI();

  useEffect(() => {
    listLocations().then(setLocations);
  }, []);

  function summaryCard(name: string, value: number | string, key?: any) {
    return (
      <Card key={key} className="w-[calc(20%-0.25rem)] p-4 flex flex-column gap-2 text-[0.9rem]">
        <div className="text-[var(--grey-500)]">{name}</div>
        <div className="font-medium text-[var(--quipli-black-800)]">
          {typeof value === 'string' ? value : displayMoneyFormat(value, currency)}
        </div>
      </Card>
    );
  }

  return (
    <div className="flex flex-wrap gap-1">
      {summaryCard('Grand Total', summary.netTotal)}
      {summaryCard('Total # of Payments', summary.paymentCount.toString())}
      {summaryCard('Charges', summary.chargeTotal)}
      {summaryCard('Refunds', summary.refundTotal)}
      {summaryCard('Failed', summary.failedTotal)}
      {summaryCard('Uncaptured', summary.uncapturedTotal)}
      {summary.methodTotals.map((t, i) => summaryCard(t.method, t.total, i))}
      {locations?.flatMap((l, i) => {
        const total = summary.locationTotals.find((t) => t.locationId === l.id)?.total;
        if (l.isActive || total) {
          return [summaryCard(l.name, summary.locationTotals.find((t) => t.locationId === l.id)?.total || 0, i)];
        } else {
          return [];
        }
      })}
    </div>
  );
};
